/**
 * 资源管理相关类型定义
 */

import type { Company as CompanyType } from './company';
import type { Position } from './position';
import type { Tag } from './tag';

/**
 * 公司状态枚举
 */
export enum CompanyStatus {
  ToApply = 'toApply',
  Applied = 'applied',
  Interviewing = 'interviewing',
  Offered = 'offered',
  Rejected = 'rejected'
}

/**
 * 公司状态显示名称
 */
export const CompanyStatusNames = {
  [CompanyStatus.ToApply]: '待投递',
  [CompanyStatus.Applied]: '已投递',
  [CompanyStatus.Interviewing]: '面试中',
  [CompanyStatus.Offered]: '已获Offer',
  [CompanyStatus.Rejected]: '已拒绝'
}

/**
 * 公司状态颜色
 */
export const CompanyStatusColors = {
  [CompanyStatus.ToApply]: 'info',
  [CompanyStatus.Applied]: 'warning',
  [CompanyStatus.Interviewing]: 'primary',
  [CompanyStatus.Offered]: 'success',
  [CompanyStatus.Rejected]: 'danger'
}

/**
 * 公司信息
 */
export interface Company {
  id: string;
  name: string;
  website: string;
  recruitUrl: string;
  notes: string;
  status: CompanyStatus;
  lastUpdated: string;
  tags: string[];
}

/**
 * 经验贴信息
 */
export interface Experience {
  id: string;
  title: string;
  url: string;
  source: string;
  tags: string[];
  notes: string;
  dateAdded: string;
}

/**
 * 添加公司请求参数
 */
export interface AddCompanyRequest {
  name: string;
  website: string;
  recruitUrl: string;
  notes: string;
  status: CompanyStatus;
  tags: string[];
}

/**
 * 添加公司响应结果
 */
export interface AddCompanyResponse {
  success: boolean;
  data: {
    id: string;
  };
  message?: string;
}

/**
 * 更新公司请求参数
 */
export interface UpdateCompanyRequest {
  name?: string;
  website?: string;
  recruitUrl?: string;
  notes?: string;
  status?: CompanyStatus;
  tags?: string[];
}

/**
 * 更新公司响应结果
 */
export interface UpdateCompanyResponse {
  success: boolean;
  message?: string;
}

/**
 * 获取公司列表响应结果
 */
export interface GetCompaniesResponse {
  success: boolean;
  data: Company[];
  message?: string;
}

/**
 * 删除公司响应结果
 */
export interface DeleteCompanyResponse {
  success: boolean;
  message?: string;
}

/**
 * 添加经验贴请求参数
 */
export interface AddExperienceRequest {
  title: string;
  url: string;
  source: string;
  tags: string[];
  notes: string;
}

/**
 * 添加经验贴响应结果
 */
export interface AddExperienceResponse {
  success: boolean;
  data: {
    id: string;
  };
  message?: string;
}

/**
 * 更新经验贴请求参数
 */
export interface UpdateExperienceRequest {
  title?: string;
  url?: string;
  source?: string;
  tags?: string[];
  notes?: string;
}

/**
 * 更新经验贴响应结果
 */
export interface UpdateExperienceResponse {
  success: boolean;
  message?: string;
}

/**
 * 获取经验贴列表响应结果
 */
export interface GetExperiencesResponse {
  success: boolean;
  data: Experience[];
  message?: string;
}

/**
 * 删除经验贴响应结果
 */
export interface DeleteExperienceResponse {
  success: boolean;
  message?: string;
}

/**
 * 资源类型枚举
 */
export enum ResourceType {
  INTERVIEW_EXPERIENCE = 'interview_experience',
  REFERRAL = 'referral',
  COMPANY_INFO = 'company_info',
  OTHER = 'other'
}

/**
 * 资源可见性枚举
 */
export enum ResourceVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private'
}

/**
 * 资源信息接口
 */
export interface Resource {
  id?: number;
  companyId: number;
  positionId?: number;
  type: ResourceType;
  title: string;
  url?: string;
  content?: string;
  visibility: ResourceVisibility;
  referralContact?: string;
  referralCode?: string;
  referralDeadline?: string;
  createTime?: string;
  updateTime?: string;
  company?: CompanyType;
  jobPosition?: Position;
  tags?: Tag[];
}

/**
 * 新增资源请求参数
 */
export interface AddResourceRequest {
  companyId: number;
  positionId?: number;
  type: ResourceType;
  title: string;
  url?: string;
  content?: string;
  visibility: ResourceVisibility;
  referralContact?: string;
  referralCode?: string;
  referralDeadline?: string;
  tagIds?: number[];
}

/**
 * 更新资源请求参数
 */
export interface UpdateResourceRequest {
  id: number;
  companyId: number;
  title?: string;
  content?: string;
  visibility?: ResourceVisibility;
  referralContact?: string;
  referralCode?: string;
  referralDeadline?: string;
  tagIds?: number[];
}

/**
 * Spring Data JPA 分页排序信息
 */
export interface Sort {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}

/**
 * Spring Data JPA 分页信息
 */
export interface Pageable {
  pageNumber: number;
  pageSize: number;
  sort: Sort;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

/**
 * Spring Data JPA 分页响应
 */
export interface PageResponse<T> {
  content: T[];
  pageable: Pageable;
  last: boolean;
  totalElements: number;
  totalPages: number;
  numberOfElements: number;
  first: boolean;
  size: number;
  number: number;
  sort: Sort;
  empty: boolean;
} 